import { api } from '@/Data/api';
import ss from '../Session';
import {
    SocialResponse,
    HashtagRecommendationType
} from './types';

/**
 * Get AI-powered hashtag recommendations based on content
 * Uses Gemini 2.0 Flash with Claude fallback for intelligent hashtag suggestions
 * @param query User content or search query (supports multiline text)
 * @param limit Maximum hashtag recommendations (default: 10)
 * @returns Promise with array of hashtag recommendations
 */
export const getHashtagRecommendations = async (query?: string, limit: number = 10): Promise<HashtagRecommendationType[] | null> => {
    try {
        const res = await api.get<SocialResponse<HashtagRecommendationType[]>>('/social/hashtag', {
            params: { query, limit },
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? res.data.data ?? null : null;
    } catch (e) {
        console.error('Failed to get hashtag recommendations:', e);
        return null;
    }
};

/**
 * Get trending hashtags for filter tabs
 * Returns top trending hashtag names for display in UI
 * @param limit Maximum trending hashtags to return
 * @returns Promise with array of hashtag names (without #)
 */
export const getTrendingHashtagNames = async (limit: number = 5): Promise<string[] | null> => {
    try {
        // Use a generic query to get trending hashtags
        const recommendations = await getHashtagRecommendations(undefined, limit);
        
        if (recommendations) {
            // Filter out new hashtags and sort by post count for trending topics
            return recommendations
                .filter(h => !h.is_new && h.post_count > 0)
                .sort((a, b) => b.post_count - a.post_count)
                .map(h => h.tag_name)
                .slice(0, limit);
        }
        
        return null;
    } catch (e) {
        console.error('Failed to get trending hashtag names:', e);
        return null;
    }
};

// Legacy compatibility exports - these now use the AI recommendation system
export const listHashtags_basic = (params: { query?: string; limit?: number } = {}) => 
    getHashtagRecommendations(params.query || 'korean learning', params.limit);
export const getHashtag = (tag_name: string) => 
    getHashtagRecommendations(tag_name, 1).then(result => result?.[0] || null);
export const listTrendingHashtags = (limit: number = 10) => getTrendingHashtagNames(limit);
export const listPopularHashtags = (limit: number = 10) => getTrendingHashtagNames(limit);
export const searchHashtags = (query: string, limit: number = 10) => getHashtagRecommendations(query, limit);
export const listHashtags = listHashtags_basic;